import { writable } from 'svelte/store';

const DEFAULT_STATE = true;

/**
 * You may either consume busyState passively (`subscribe()`) or register for
 * (b)locking it in busy mode.
 * To perform simple asynchronous tasks, use `exec()` passing your (async)
 * callback and any parameters it requires – you will receive your callback's
 * result and the busy state will remain TRUE while it executes.
 * For more complex operations or multiple async calls you can use the `block()`
 * method – it will (b)lock the busy state in TRUE indefinitely – until you call
 * the unblock callback you received from `block()`.
 */
class BusyState {
    constructor() {
        this.queue = [];
        this.isBusy = DEFAULT_STATE;
        // this.initComplete = false;

        this.store = writable(DEFAULT_STATE);

        this._listen();
    }

    _listen() {
        this.store.subscribe((busy) => {
            if (busy !== this.isBusy) {
                if (this.queue.length) {
                    this._setState(true);
                } else {
                    this._setState(false);
                }
            }
        });
    }

    _setState(busy = true) {
        this.isBusy = busy;
        this.store.update(() => busy);
    }

    _onFinished() {
        this.queue.pop();
        if (!this.queue.length) {
            this._setState(false);
        }
    }

    /**
     * Keep the spinner running while an asynchronous operation is running.
     *
     * @param callback
     * @param args
     * @returns {Promise<void>}
     */
    async exec(callback, ...args) {
        if (!this.isBusy) {
            this._setState(true);
        }
        this.queue.push(0);
        const result = await callback(...args);
        this._onFinished();
        return result;
    }

    /**
     * Block the spinner on active state indefinitely, until you call the returned
     * callback function to indicate you're done.
     *
     * @returns {function(): void}
     */
    block() {
        if (!this.isBusy) {
            this._setState(true);
        }
        this.queue.push(0);
        return () => {
            this._onFinished();
        };
    }

    get subscribe() {
        return this.store.subscribe;
    }
}

export const busyState = new BusyState();
